// Terms of Service & Privacy Policy Consent Service
// Manages consent data storage, versioning, and validation for terms and privacy policy acceptance

import { debug } from '../utils/debug';

export interface ConsentData {
  consented: boolean;
  version: string;
  timestamp: string;
  email?: string; // For local storage before account creation
  ageVerified?: boolean; // Age verification status
  termsAccepted?: boolean; // Terms of service acceptance
}

export interface ConsentConfig {
  currentVersion: string;
  requiredForApp: boolean;
}

// Current consent version - increment this when policy changes
export const CONSENT_CONFIG: ConsentConfig = {
  currentVersion: '1.2.0',
  requiredForApp: true
};

// Local storage keys
const CONSENT_STORAGE_KEY = 'termsPrivacyConsent';
const CONSENT_BY_EMAIL_KEY = 'consentByEmail';

/**
 * Get consent data from local storage
 */
export const getLocalConsent = (): ConsentData | null => {
  try {
    const stored = localStorage.getItem(CONSENT_STORAGE_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch (error) {
    console.error('Error reading consent from localStorage:', error);
    return null;
  }
};

/**
 * Store consent data in local storage
 */
export const setLocalConsent = (consentData: ConsentData): void => {
  try {
    localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(consentData));
  } catch (error) {
    console.error('Error storing consent in localStorage:', error);
  }
};

/**
 * Store consent data by email (for pre-signup consent)
 */
export const setConsentByEmail = (email: string, consentData: ConsentData): void => {
  try {
    const existingData = getConsentByEmail();
    const updatedData = {
      ...existingData,
      [email.toLowerCase()]: consentData
    };
    localStorage.setItem(CONSENT_BY_EMAIL_KEY, JSON.stringify(updatedData));
  } catch (error) {
    console.error('Error storing consent by email:', error);
  }
};

/**
 * Get consent data by email
 */
export const getConsentByEmail = (email?: string): Record<string, ConsentData> | ConsentData | null => {
  try {
    const stored = localStorage.getItem(CONSENT_BY_EMAIL_KEY);
    const data = stored ? JSON.parse(stored) : {};
    
    if (email) {
      return data[email.toLowerCase()] || null;
    }
    
    return data;
  } catch (error) {
    console.error('Error reading consent by email:', error);
    return null;
  }
};

/**
 * Check if user has valid consent
 */
export const hasValidConsent = (email?: string): boolean => {
  // First check if we have consent for this specific email
  if (email) {
    const emailConsent = getConsentByEmail(email) as ConsentData;
    if (emailConsent && emailConsent.consented && emailConsent.version === CONSENT_CONFIG.currentVersion) {
      return true;
    }
    // For email-specific checks, don't fall back to general consent
    return false;
  }
  
  // Fallback to general consent only when no email is provided
  const generalConsent = getLocalConsent();
  return generalConsent?.consented === true && generalConsent?.version === CONSENT_CONFIG.currentVersion;
};

/**
 * Check if consent needs to be updated (different version)
 */
export const needsConsentUpdate = (email?: string): boolean => {
  if (email) {
    const emailConsent = getConsentByEmail(email) as ConsentData;
    return !emailConsent || emailConsent.version !== CONSENT_CONFIG.currentVersion;
  }
  
  const generalConsent = getLocalConsent();
  return !generalConsent || generalConsent.version !== CONSENT_CONFIG.currentVersion;
};

/**
 * Create consent data object
 */
export const createConsentData = (consented: boolean, email?: string, ageVerified?: boolean, termsAccepted?: boolean): ConsentData => {
  return {
    consented,
    version: CONSENT_CONFIG.currentVersion,
    timestamp: new Date().toISOString(),
    email: email?.toLowerCase(),
    ageVerified,
    termsAccepted
  };
};

/**
 * Clear all consent data
 */
export const clearConsentData = (): void => {
  try {
    localStorage.removeItem(CONSENT_STORAGE_KEY);
    localStorage.removeItem(CONSENT_BY_EMAIL_KEY);
  } catch (error) {
    console.error('Error clearing consent data:', error);
  }
};

/**
 * Clear all consent data (for debugging)
 */
export const clearAllConsentData = (): void => {
  try {
    localStorage.removeItem(CONSENT_STORAGE_KEY);
    localStorage.removeItem(CONSENT_BY_EMAIL_KEY);
    localStorage.removeItem('cookieConsent'); // Remove old key too
    localStorage.removeItem('termsPrivacyConsent'); // Remove old key too
    debug.log('All consent data cleared');
  } catch (error) {
    console.error('Error clearing all consent data:', error);
  }
};

/**
 * Migrate old consent data to new format if needed
 */
export const migrateConsentData = (): void => {
  try {
    const oldConsent = localStorage.getItem('cookieConsent');
    const oldTermsConsent = localStorage.getItem('termsPrivacyConsent');
    
    if (oldConsent && typeof oldConsent === 'string') {
      // Check if it's the old format (just a string or simple object)
      const parsed = JSON.parse(oldConsent);
      if (parsed && typeof parsed === 'object' && !parsed.version) {
        // Migrate old format to new format
        const newConsentData = createConsentData(parsed.consented || true, undefined, true, true);
        setLocalConsent(newConsentData);
        debug.log('Migrated old cookie consent data to new terms/privacy format');
      }
    }
    
    if (oldTermsConsent && typeof oldTermsConsent === 'string') {
      // Check if it's the old format (just a string or simple object)
      const parsed = JSON.parse(oldTermsConsent);
      if (parsed && typeof parsed === 'object' && !parsed.version) {
        // Migrate old format to new format
        const newConsentData = createConsentData(parsed.consented || true, undefined, true, true);
        setLocalConsent(newConsentData);
        debug.log('Migrated old terms consent data to new format');
      }
    }
    
    // Clear any old general consent data to prevent interference with email-specific consent
    // This ensures new users always get the consent modal
    const generalConsent = getLocalConsent();
    if (generalConsent && generalConsent.version !== CONSENT_CONFIG.currentVersion) {
      clearConsentData();
      debug.log('Cleared outdated general consent data');
    }
  } catch (error) {
    console.error('Error migrating consent data:', error);
  }
};
